import { onBeforeUnmount, ref, watch } from '@vue/composition-api';
import { handleRequests } from '@/helpers';
import axios from 'axios';

/**
 * @typedef {object} FrequentAddressLite - A lite version of the frequent address object.
 * @property {number} frequentAddressId
 * @property {null} customerId
 * @property {string} address
 * @property {string} addressCode
 */

export default function useFrequentAddressQuery({ searchKey, customerId }) {
    /**
     * State machine way of tracking status
     * |> idle -> searching -> done -> idle
     */
    const searchStatus = ref('idle');
    /**
     *
     * @type {import("@vue/composition-api").Ref<FrequentAddressLite[]>}
     */
    const frequentAddresses = ref([]);
    const cancelSource = ref(null);

    function sendCancelSignal() {
        if (cancelSource.value) {
            cancelSource.value.cancel('Operation cancelled: unmounting or new request');
            cancelSource.value = null;
        }
        cancelSource.value = axios.CancelToken.source();
    }
    onBeforeUnmount(() => {
        sendCancelSignal();
    });

    async function fetchFrequentAddresses() {
        searchStatus.value = 'searching';
        const searchParams = new URLSearchParams();
        searchParams.set('query', searchKey.value);
        searchParams.set('customerId', customerId.value);

        const api = `/api/frequent-addresses?${searchParams.toString()}`;
        const payload = {
            method: 'get',
            cancelToken: cancelSource.value.token
        };
        const response = await handleRequests(api, payload);
        searchStatus.value = 'done';
        if (!response.data) {
            return;
        }
        frequentAddresses.value = response.data;
    }

    /**
     *
     * @param {number} frequentAddressId
     * @returns {Promise<import("@/jsDocTypes/FrequentAddress").FrequentAddress>}
     */
    async function getSingleFrequentAddress(frequentAddressId) {
        const api = `/api/frequent-addresses/${frequentAddressId}`;
        const response = await handleRequests(api, { method: 'get' });
        return response.data;
    }

    function resetSearchStatus() {
        searchStatus.value = 'idle';
    }

    watch([() => customerId.value, () => searchKey.value], () => {
        if (!searchKey?.value) {
            return;
        }
        sendCancelSignal();
        fetchFrequentAddresses();
    });

    return {
        searchStatus,
        frequentAddresses,
        resetSearchStatus,
        getSingleFrequentAddress
    };
}
